/**
 * BriefingNoteDialog (ADR-0008).
 *
 * Modal that captures the operator-supplied metadata + Yes/No
 * judgements before producing the briefing-note .docx. Pre-fills
 * from the API's /briefing-note/context endpoint so the operator
 * does not retype data the platform already knows.
 *
 * Layout has two collapsible sections:
 *  - Document metadata (parties, contract title, variation clause,
 *    submission ref, sign-off names).
 *  - Operator judgements (the Yes/No questions the determination
 *    deliberately leaves to the contract administrator).
 *
 * Submit POSTs to /briefing-note/render which returns the .docx
 * bytes; the dialog triggers the browser download and closes.
 */

import { useEffect, useState } from "react";
import {
  getBriefingNoteContext,
  loadConfig,
  renderBriefingNote,
  type ApiError,
  type BriefingNoteRenderRequest,
  type JudgementAnswerPayload,
} from "../api/client";
import { Icon } from "../icons";
import { useToast } from "../Toasts";

interface JudgementFieldDef {
  key: keyof Pick<
    BriefingNoteRenderRequest,
    | "super_proposed"
    | "for_convenience"
    | "mitigation_taken"
    | "scheduling_assessed"
    | "full_package_quoted"
  >;
  question: string;
  section: string;
}

const JUDGEMENT_FIELDS: readonly JudgementFieldDef[] = [
  {
    key: "super_proposed",
    question: "Has the Superintendent proposed a variation?",
    section: "Variations · Q3",
  },
  {
    key: "for_convenience",
    question: "Is it a variation for the Contractor's convenience?",
    section: "Variations · Q6",
  },
  {
    key: "mitigation_taken",
    question:
      "Has the Contractor taken all reasonable steps to mitigate the cause of delay?",
    section: "EOT · Q8",
  },
  {
    key: "scheduling_assessed",
    question:
      "Has the Scheduling team completed an assessment of any impact to the PC/Completion date, EOT and/or Variation?",
    section: "Schedule · Q1",
  },
  {
    key: "full_package_quoted",
    question: "Has the work been quoted as a full package of work?",
    section: "Commercial · Q5",
  },
] as const;

const EMPTY_JUDGEMENT: JudgementAnswerPayload = { answer: null, comment: "" };

function defaultRenderRequest(): BriefingNoteRenderRequest {
  return {
    principal_entity: "",
    contractor_entity: "",
    contract_title: "",
    contract_reference: "",
    variation_clause: "40.1",
    variation_submission_ref: "",
    prepared_by: "",
    project_manager: "",
    super_proposed: { ...EMPTY_JUDGEMENT },
    for_convenience: { ...EMPTY_JUDGEMENT },
    mitigation_taken: { ...EMPTY_JUDGEMENT },
    scheduling_assessed: { ...EMPTY_JUDGEMENT },
    full_package_quoted: { ...EMPTY_JUDGEMENT },
  };
}

interface BriefingNoteDialogProps {
  assessmentId: string;
  variationId: string;
  open: boolean;
  onClose: () => void;
}

export function BriefingNoteDialog({
  assessmentId,
  variationId,
  open,
  onClose,
}: BriefingNoteDialogProps): React.JSX.Element | null {
  const toast = useToast();
  const [form, setForm] = useState<BriefingNoteRenderRequest>(
    defaultRenderRequest,
  );
  const [loadingContext, setLoadingContext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch suggested defaults the first time the dialog opens for a
  // given assessment. We reset the form on open so re-opening for a
  // different assessment does not show the previous one's data.
  useEffect(() => {
    if (!open) return;
    const cfg = loadConfig();
    setLoadingContext(true);
    getBriefingNoteContext(cfg, assessmentId)
      .then((ctx) => {
        setForm((prev) => ({
          ...prev,
          principal_entity: ctx.principal_entity || prev.principal_entity,
          contractor_entity: ctx.contractor_entity || prev.contractor_entity,
          contract_title: ctx.contract_title || prev.contract_title,
          contract_reference: ctx.contract_reference || prev.contract_reference,
          variation_clause: ctx.variation_clause || prev.variation_clause,
          variation_submission_ref:
            ctx.variation_submission_ref || prev.variation_submission_ref,
          prepared_by: ctx.prepared_by || prev.prepared_by,
          project_manager: ctx.project_manager || prev.project_manager,
        }));
      })
      .catch(() => {
        // Pre-fill failure is non-fatal; the operator can fill the
        // form by hand. We deliberately don't toast — the dialog
        // is already showing empty defaults which is itself the
        // signal something went wrong.
      })
      .finally(() => setLoadingContext(false));
  }, [open, assessmentId]);

  if (!open) return null;

  const setMeta = <K extends keyof BriefingNoteRenderRequest>(
    field: K,
    value: BriefingNoteRenderRequest[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setJudgement = (
    field: JudgementFieldDef["key"],
    next: JudgementAnswerPayload,
  ) => {
    setForm((prev) => ({ ...prev, [field]: next }));
  };

  const onGenerate = async () => {
    setSubmitting(true);
    try {
      const cfg = loadConfig();
      const blob = await renderBriefingNote(cfg, assessmentId, form);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `briefing-note-${variationId || assessmentId}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.show("ok", "Briefing note generated.");
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      toast.show("err", `Generate failed: ${apiErr.message ?? "unknown"}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Generate Briefing Note"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, maxHeight: "90vh" }}
      >
        <div className="sheet-head">
          <div className="sheet-title">
            Generate Briefing Note · {variationId || assessmentId}
          </div>
          <button
            className="btn-icon"
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <Icon.cross />
          </button>
        </div>
        <div className="sheet-body" style={{ overflowY: "auto" }}>
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
            The engine populates the determination's findings; the fields below
            are the items the platform cannot derive on its own. Anything left
            blank renders with a bracketed placeholder or "Not assessed" in the
            generated document.
          </p>

          {loadingContext && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Pre-filling from contract record…
            </div>
          )}

          <fieldset
            style={{
              border: "0.5px solid var(--border)",
              borderRadius: 6,
              padding: 12,
              margin: "12px 0",
            }}
          >
            <legend
              style={{
                fontWeight: 600,
                padding: "0 6px",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--text-muted)",
              }}
            >
              Document metadata
            </legend>
            <p
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              Fields show the platform's best guess from your contract
              record. Anything blank below is not in the platform's
              data; type the value in or the document will render
              that cell empty.
            </p>
            <TextField
              label="Principal (Row 1 of the document header)"
              hint="Legal entity, e.g. Acme Holdings Pty Ltd (ACN 000 000 000)"
              value={form.principal_entity}
              onChange={(v) => setMeta("principal_entity", v)}
            />
            <TextField
              label="Contractor (Row 2 of the document header)"
              hint="Legal entity, e.g. Beta Construction Pty Ltd (ABN 00 000 000 000)"
              value={form.contractor_entity}
              onChange={(v) => setMeta("contractor_entity", v)}
            />
            <TextField
              label="Contract Name (Row 3)"
              hint="Descriptive name, e.g. Project Apollo Glass Production Building LV Installation"
              value={form.contract_title}
              onChange={(v) => setMeta("contract_title", v)}
            />
            <TextField
              label="Contract No (Row 4)"
              hint="Form of contract + date, e.g. AS 4300 dated 04 July 2025"
              value={form.contract_reference}
              onChange={(v) => setMeta("contract_reference", v)}
            />
            <TextField
              label="Variation clause (RE: line)"
              hint="Clause the variation is directed under, e.g. 40.1"
              value={form.variation_clause}
              onChange={(v) => setMeta("variation_clause", v)}
            />
            <TextField
              label="Variation submission reference"
              hint="Contractor's submission id, e.g. NV34.1 dated 10 February 2026"
              value={form.variation_submission_ref}
              onChange={(v) => setMeta("variation_submission_ref", v)}
            />
            <TextField
              label="Project Manager (sign-off block)"
              hint="Person signing the Project Manager review line"
              value={form.project_manager}
              onChange={(v) => setMeta("project_manager", v)}
            />
            <TextField
              label="Contract Administrator (sign-off block)"
              hint="Person signing the Contract Administrator line"
              value={form.prepared_by}
              onChange={(v) => setMeta("prepared_by", v)}
            />
          </fieldset>

          <fieldset
            style={{
              border: "0.5px solid var(--border)",
              borderRadius: 6,
              padding: 12,
              margin: "12px 0",
            }}
          >
            <legend
              style={{
                fontWeight: 600,
                padding: "0 6px",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--text-muted)",
              }}
            >
              Operator judgement (optional)
            </legend>
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 12px" }}>
              These rows sit outside the engine's determination and are
              traditionally completed by the contract administrator. Leave any
              row unset to render it blank for hand-completion in Word.
            </p>
            {JUDGEMENT_FIELDS.map((def) => (
              <JudgementField
                key={def.key}
                question={def.question}
                section={def.section}
                value={form[def.key]}
                onChange={(next) => setJudgement(def.key, next)}
              />
            ))}
          </fieldset>

          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void onGenerate()}
              disabled={submitting}
              data-testid="generate-briefing-note"
            >
              {submitting ? "Generating…" : "Generate & Download"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /**
   * Helper caption shown ABOVE the input. We deliberately do not
   * use an HTML ``placeholder`` for example values: operators
   * mistake greyed-out placeholder text for the real input value
   * and submit an empty form, which then renders bracketed
   * "[Contractor Name]" defaults in the document.
   */
  hint?: string;
}) {
  return (
    <div className="field" style={{ marginBottom: 10 }}>
      <label>{label}</label>
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            marginBottom: 4,
          }}
        >
          {hint}
        </div>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "0.5px solid var(--border-strong)",
          borderRadius: 6,
          fontSize: 13,
        }}
      />
    </div>
  );
}

function JudgementField({
  question,
  section,
  value,
  onChange,
}: {
  question: string;
  section: string;
  value: JudgementAnswerPayload;
  onChange: (next: JudgementAnswerPayload) => void;
}) {
  const setAnswer = (answer: boolean | null) => onChange({ ...value, answer });
  const setComment = (comment: string) => onChange({ ...value, comment });

  return (
    <div
      style={{
        padding: "8px 0",
        borderBottom: "0.5px solid var(--border)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--text-muted)",
          marginBottom: 2,
        }}
      >
        {section}
      </div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{question}</div>
      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 6 }}>
        {(
          [
            ["Yes", true],
            ["No", false],
            ["Skip", null],
          ] as const
        ).map(([label, optionValue]) => (
          <label
            key={String(optionValue)}
            style={{ fontSize: 13, display: "flex", gap: 4, alignItems: "center" }}
          >
            <input
              type="radio"
              checked={value.answer === optionValue}
              onChange={() => setAnswer(optionValue)}
            />
            {label}
          </label>
        ))}
      </div>
      <input
        type="text"
        value={value.comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Comment (optional)"
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "0.5px solid var(--border-strong)",
          borderRadius: 6,
          fontSize: 12,
        }}
      />
    </div>
  );
}
